/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from '@emotion/styled'
import { pick } from 'lodash'
import { physicianFormalName } from 'helpers/nameHelper'

import { useGetLabPhysicianListingQuery } from 'store/services/labPhysician'
import DataTable from 'components/atoms/DataTable'
import { TableWrapper } from 'components/atoms/TableWrapper'
import DashboardContent from '../atoms/DashboardContent'
import { handleChange } from 'components/atoms/Upsert'
import Input from 'components/atoms/Input'
import Link from 'components/atoms/Link'
import Button from 'components/atoms/Button'
import routes from '../constants/routes'
import Select from 'components/atoms/Select'
import { PhysicianType, ProviderType } from 'store/types'
import { PhysicianModal } from './PhysicianModal';
import { isOrganization } from '../../../helpers/followUpProvidersHelper';
import states from 'constants/states';

const StyledTableWrapper = styled(TableWrapper)`
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.6rem;
    div {
      span {
        font-size: 0.9rem;
      }
    }
  }
`
const Physicians = () => {
  const navigate = useNavigate()
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(0)
  const [sortOptions, setSortOptions] = useState({})
  const [showAddModal, setShowAddModal] = useState(false)
  const [values, setValues] = useState({
    column: '',
    firstName: '',
    lastName: '',
    organizationName: '',
    npi: '',
    addressStreetLine1: '',
    addressCity: '',
    addressState: '',
    telephone: '',
    fax: '',
    licensedIn: '',
    physicianType: '',
  })

  const { data: physicians, isLoading, refetch } = useGetLabPhysicianListingQuery({
    ...pick(values, [
      'firstName',
      'lastName',
      'organizationName',
      'npi',
      'addressStreetLine1',
      'addressCity',
      'addressState',
      'telephone',
      'fax',
      'licensedIn',
      'physicianType',
    ]),
    page: currentPage,
    size: pageSize,
    sortField: sortOptions?.id,
    sortOrder: sortOptions?.desc ? 'desc' : 'asc',
  })

  const header = [
    {
      accessor: 'npi',
      to: `${routes.index}${routes.physician.root}${routes.physician.upsert}`,
      Header: 'NPI',
    },
    {
      accessor: 'providerType',
      Header: 'Provider Type',
      Cell: ({ row }) => (
        <div>{isOrganization(row.original.providerType) ? 'Organization' : 'Individual'}</div>
      ),
    },
    {
      accessor: 'physicianType',
      Header: 'Physician Type',
      Cell: ({ row }) => {
        const labels: Record<string, string> = {
          REFERRING: 'Referring',
          FOLLOW_UP: 'Follow-up',
          INTERPRETING: 'Interpreting',
        }
        return <div>{labels[row.original.physicianType] ?? row.original.physicianType ?? '—'}</div>
      },
    },
    {
      accessor: 'name',
      Header: 'Name',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div css={{ position: 'relative' }}>
          <div>
            {row.original.providerType === ProviderType.ORGANIZATION
              ? row.original.organizationName
              : physicianFormalName(row.original.firstName, row.original.lastName, row.original.credential)}
            <Link
              className="open-new-tab"
              css={{ display: 'none', position: 'absolute', right: '0', top: 0 }}
              to={`${routes.index}${routes.physician.root}${routes.physician.upsert}${row.original.npi}`}
              target="_blank"
              icon="fas fa-external-link-alt"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      ),
    },
    {
      accessor: 'offices',
      Header: 'Offices',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div>
          {row.original.offices?.map((office, index) => (
            <div key={index}>{office?.officeName || '-'}</div>
          ))}
        </div>
      ),
    },
    {
      accessor: 'addresses',
      Header: 'Addresses',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div>
          {row.original.addresses?.map((address, index) => (
            <div key={index}>
              {address.addressStreetLine1} {address.addressStreetLine2}, {address.addressCity},{' '}
              {address.addressState} {address.addressZip}
            </div>
          ))}
        </div>
      ),
    },
  ]

  return (
    <>
      <PhysicianModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false)
          refetch()
        }}
      />
      <DashboardContent
        title="Physicians"
        icon="fa fa-hdd"
        content={
          <div className="card card-primary">
            <form onSubmit={() => {}}>
              <div
                className="card-header"
                css={{ borderBottom: 'none', position: 'relative', zIndex: '999' }}>
                <div className="row">
                  <div className="col input-group">
                    <Input
                      autoFocus={true}
                      value={values.firstName}
                      label={false}
                      placeholder="First Name"
                      name="firstName"
                      parentDiv={null}
                      handleChange={(event) => {
                        handleChange(event, values, setValues)
                      }}
                    />{' '}
                  </div>
                  <div className="col input-group">
                    <Input
                      autoFocus={true}
                      value={values.lastName}
                      label={false}
                      placeholder="Last Name"
                      name="lastName"
                      parentDiv={null}
                      handleChange={(event) => {
                        handleChange(event, values, setValues)
                      }}
                    />{' '}
                  </div>
                  <div className="col input-group">
                    <Input
                      autoFocus={true}
                      value={values.organizationName}
                      label={false}
                      placeholder="Organization Name"
                      name="organizationName"
                      parentDiv={null}
                      handleChange={(event) => {
                        handleChange(event, values, setValues)
                      }}
                    />{' '}
                  </div>
                  <div className="col input-group">
                    <Input
                      autoFocus={true}
                      value={values.npi}
                      label={false}
                      placeholder="NPI"
                      name="npi"
                      parentDiv={null}
                      handleChange={(event) => {
                        handleChange(event, values, setValues)
                      }}
                    />{' '}
                  </div>
                  <div className="col-sm-2" style={{ maxWidth: '140px'}}>
                    <Select
                      name="pageSize"
                      className="basic-single w-100 form-control"
                      handleChange={(e) => setPageSize(Number(e.target.value))}
                      options={['20', '50', '100'].map((i) => ({ value: i, label: i }))}
                      value={pageSize}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col input-group mb-3">
                    <Input
                      autoFocus={true}
                      value={values.addressStreetLine1}
                      label={false}
                      placeholder="Street Address"
                      name="addressStreetLine1"
                      parentDiv={null}
                      handleChange={(event) => {
                        handleChange(event, values, setValues)
                      }}
                    />{' '}
                  </div>
                  <div className="col input-group mb-3">
                    <Input
                      autoFocus={true}
                      value={values.addressCity}
                      label={false}
                      placeholder="City"
                      name="addressCity"
                      parentDiv={null}
                      handleChange={(event) => {
                        handleChange(event, values, setValues)
                      }}
                    />{' '}
                  </div>
                  <div className="col input-group mb-3">
                    <Input
                      autoFocus={true}
                      value={values.addressState}
                      label={false}
                      placeholder="State"
                      name="addressState"
                      parentDiv={null}
                      handleChange={(event) => {
                        handleChange(event, values, setValues)
                      }}
                    />{' '}
                  </div>
                  <div className="col input-group mb-3">
                    <Input
                      autoFocus={true}
                      value={values.telephone}
                      label={false}
                      placeholder="Phone"
                      name="telephone"
                      parentDiv={null}
                      handleChange={(event) => {
                        handleChange(event, values, setValues)
                      }}
                    />{' '}
                  </div>
                  <div className="col input-group mb-3">
                    <Input
                      autoFocus={true}
                      value={values.fax}
                      label={false}
                      placeholder="Fax"
                      name="fax"
                      parentDiv={null}
                      handleChange={(event) => {
                        handleChange(event, values, setValues)
                      }}
                    />{' '}
                  </div>
                  <div className="col input-group mb-3">
                    <select
                      className="form-control"
                      value={values.licensedIn}
                      onChange={(e) => setValues({ ...values, licensedIn: e.target.value })}>
                      <option value="">Licensed In (All States)</option>
                      {states.filter((s) => s.id).map((s) => (
                        <option key={s.id} value={s.id}>{s.name} ({s.id})</option>
                      ))}
                    </select>
                  </div>
                  <div className="col input-group mb-3">
                    <select
                      className="form-control"
                      value={values.physicianType}
                      onChange={(e) => setValues({ ...values, physicianType: e.target.value })}>
                      <option value="">Physician Type (All)</option>
                      <option value={PhysicianType.REFERRING}>Referring</option>
                      <option value={PhysicianType.FOLLOW_UP}>Follow-up</option>
                      <option value={PhysicianType.INTERPRETING}>Interpreting</option>
                    </select>
                  </div>
                  <div className="text-right mb-3 px-sm-2">
                    <Button
                      className="btn btn-primary"
                      label="Add Physician"
                      onClick={(e) => {
                        e.preventDefault()
                        setShowAddModal(true)
                      }}>
                      <i className="fa fa-plus-circle mr-2" />
                    </Button>
                  </div>
                </div>
              </div>
              <StyledTableWrapper
                className="card-body row"
                css={{
                  '& [role=row]:hover': {
                    '& .open-new-tab': {
                      display: 'block',
                    },
                  },
                }}>
                <DataTable
                  tableKey={'lab_physicians'}
                  data={physicians}
                  status={isLoading}
                  tableColumns={header}
                  pageSize={pageSize}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                  setSortOptions={setSortOptions}
                  entityName="physician"
                  onRowClick={(row) => {
                    navigate(
                      `${routes.index}${routes.physician.root}${routes.physician.upsert}${row.original.npi}`,
                    )
                  }}
                />
              </StyledTableWrapper>
            </form>
          </div>
        }
        breadcrumb={[{ label: 'Physicians' }]}
      />
    </>
  )
}

export default Physicians
